import React, { useState } from 'react';
import styled from 'styled-components';
import {
  AuthFieldContainer,
  AuthFieldLabel,
  AuthFormContainer,
  AuthFormHint,
  AuthInputField,
  AuthSubmitButton,
} from '../LoginPagesUI';
import Cognito from '../../utils/Cognito';
import { AxiosError } from 'axios';
import { ICognitoResponse, ILoginInput, LoginStep } from '../types';
import FieldError from '../../../../components/Form/FieldError';
import { TOAST_ERROR_MESSAGE } from '../../utils/utils';
import { goBackToLoginStep } from './utils';
import { Updater } from 'use-immer';
import toastMessage from '../../../../utils/toastMessage';

interface IProps {
  setLoginStep: (step: LoginStep) => void;
  setLoginInput: Updater<ILoginInput>;
  loginInput: ILoginInput;
}

const StyledHint = styled(AuthFormHint)`
  margin-bottom: 32px;
`;

const ForgotPasswordForm = ({
  setLoginStep,
  setLoginInput,
  loginInput,
}: IProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const handleForgotPassword = () => {
    setIsSubmitting(true);
    Cognito.forgotPassword(loginInput.email)
      .then(() => {
        toastMessage({
          message: 'Check your email for the password reset code.',
          type: 'info',
        });
        setLoginStep(LoginStep.ConfirmForgotPassword);
      })
      .catch((err: AxiosError<ICognitoResponse>) => {
        setIsSubmitting(false);
        const status = err.response?.data.status;
        switch (status) {
          case 400:
            setHasError(true);
            return;
          default:
            toastMessage({
              message: TOAST_ERROR_MESSAGE,
              type: 'error',
            });
            goBackToLoginStep({
              setLoginStep,
              setLoginInput,
            });
            return;
        }
      });
  };

  const onEmailBlur = () => {
    if (hasError) {
      setHasError(false);
    }
  };

  const onEmailChange = (value: string) => {
    setLoginInput((draft) => {
      draft.email = value;
    });
  };

  return (
    <>
      <StyledHint>
        Enter your account email and we will send you a message for the password
        recovery process.
      </StyledHint>
      <AuthFormContainer>
        <AuthFieldContainer>
          <AuthFieldLabel>Email</AuthFieldLabel>
          <AuthInputField
            type="text"
            placeholder={'Enter your email'}
            value={loginInput.email}
            onChange={onEmailChange}
            autoComplete={'username'}
            onBlur={onEmailBlur}
          />
        </AuthFieldContainer>
        {hasError && (
          <FieldError
            type="custom"
            customError={'Please enter a valid email address.'}
          />
        )}
        <AuthSubmitButton
          type="submit"
          size="default"
          loading={isSubmitting}
          onClick={handleForgotPassword}
          trackContext="login"
          trackAction="forgot password - send email"
        >
          Send Email
        </AuthSubmitButton>
      </AuthFormContainer>
    </>
  );
};

export default ForgotPasswordForm;
